import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    Upload, Download, FileText, CheckCircle, AlertCircle,
    ChevronDown, ChevronUp, X, ArrowRight, Check,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// ─── CSV templates ────────────────────────────────────────────────────────────

const CONTACT_TEMPLATE = [
    "name*,type,email,phone,company,role,tags,sectors,geographies,ebitda_min,ebitda_max,notes",
    'Acorn Capital Partners,buyer,contact@acorncapital.co.uk,020 7123 4567,Acorn Capital,Managing Director,"PE, Strategic Buyer","Manufacturing, Engineering","UK, Germany",500000,3000000,Active acquirer in UK manufacturing',
    "Hartwell Group,buyer,info@hartwellgroup.com,,Hartwell Group,Director,Strategic Buyer,Healthcare,\"UK, Ireland\",250000,1500000,",
    "Bright Advisory Ltd,intermediary,hello@brightadvisory.co.uk,01234 567890,Bright Advisory,Partner,Accountant,,,,,Regular introducer",
].join("\n");

const DEAL_TEMPLATE = [
    "company_name*,sector,stage,deal_size,ebitda,revenue,fee,location,origination_source,notes,expected_close",
    "Meridian Engineering Ltd,Manufacturing,Mandated,4500000,620000,3200000,135000,West Midlands,accountant,Strong EBITDA growth over 3 years,31/12/2026",
    "Brightside Care Group,Healthcare,NDA Signed,2800000,380000,1900000,,Yorkshire,referral,,28/02/2027",
    "TechFlow Solutions,SaaS,Origination,8000000,1100000,5500000,,London,linkedin,Early stage - owner exploring options,",
].join("\n");

const CONTACT_FIELDS = [
    { value: "name", label: "Name *" },
    { value: "type", label: "Type" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "company", label: "Company" },
    { value: "role", label: "Role" },
    { value: "tags", label: "Tags" },
    { value: "sectors", label: "Sectors" },
    { value: "geographies", label: "Geographies" },
    { value: "ebitda_min", label: "EBITDA Min" },
    { value: "ebitda_max", label: "EBITDA Max" },
    { value: "notes", label: "Notes" },
];

const DEAL_FIELDS = [
    { value: "company_name", label: "Company Name *" },
    { value: "sector", label: "Sector" },
    { value: "stage", label: "Stage" },
    { value: "deal_size", label: "Deal Size" },
    { value: "ebitda", label: "EBITDA" },
    { value: "revenue", label: "Revenue" },
    { value: "fee", label: "Fee" },
    { value: "location", label: "Location" },
    { value: "origination_source", label: "Origination Source" },
    { value: "notes", label: "Notes" },
    { value: "expected_close", label: "Expected Close" },
];

function downloadTemplate(filename, content) {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// ─── DropZone ─────────────────────────────────────────────────────────────────

function DropZone({ onFile, selectedFile, onClear, disabled }) {
    const inputRef = useRef(null);
    const [dragging, setDragging] = useState(false);

    const handleFile = (f) => {
        if (!f) return;
        if (!f.name.endsWith(".csv")) { toast.error("Please upload a .csv file"); return; }
        onFile(f);
    };

    return (
        <div
            className={`border-2 border-dashed rounded-md p-5 text-center cursor-pointer transition-colors ${
                dragging ? "border-foreground bg-accent/30" : "border-border hover:border-foreground/40"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => !disabled && !selectedFile && inputRef.current?.click()}
            data-testid="import-dropzone"
        >
            <input ref={inputRef} type="file" accept=".csv" className="hidden"
                onChange={(e) => handleFile(e.target.files[0])} disabled={disabled} />
            {selectedFile ? (
                <div className="flex items-center justify-center gap-2 text-sm font-medium">
                    <FileText className="w-4 h-4 text-foreground" />
                    <span>{selectedFile.name}</span>
                    <button type="button" className="text-muted-foreground hover:text-destructive ml-1"
                        onClick={(e) => { e.stopPropagation(); onClear(); }}>
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <div className="space-y-1">
                    <Upload className="w-5 h-5 text-muted-foreground mx-auto" />
                    <p className="text-sm text-muted-foreground">
                        Drag & drop a CSV here, or <span className="text-foreground underline">click to browse</span>
                    </p>
                    <p className="text-xs text-muted-foreground">.csv files only</p>
                </div>
            )}
        </div>
    );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({ label }) {
    return (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <svg className="animate-spin w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
            </svg>
            {label}
        </div>
    );
}

// ─── ImportResult ─────────────────────────────────────────────────────────────

function ImportResult({ result, onReset }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400" data-testid="import-success-count">
                <CheckCircle className="w-4 h-4" />
                {result.imported} {result.imported === 1 ? "record" : "records"} imported successfully
            </div>
            {result.skipped > 0 && (
                <div className="space-y-1">
                    <button className="flex items-center gap-2 text-sm font-medium text-amber-600 w-full text-left"
                        onClick={() => setExpanded((v) => !v)} data-testid="import-skipped-toggle">
                        <AlertCircle className="w-4 h-4" />
                        {result.skipped} {result.skipped === 1 ? "row" : "rows"} skipped
                        {expanded ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                    </button>
                    {expanded && (
                        <ul className="text-xs text-muted-foreground border border-border/60 rounded-md divide-y divide-border/40 overflow-hidden">
                            {result.skipped_details.map((d, i) => (
                                <li key={i} className="px-3 py-2 flex justify-between gap-4">
                                    <span className="font-medium truncate">{d.name || <em>unnamed</em>}</span>
                                    <span className="text-muted-foreground shrink-0">Row {d.row} — {d.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            <Button size="sm" variant="outline" onClick={onReset} data-testid="import-another-btn">
                Import another file
            </Button>
        </div>
    );
}

// ─── MappingStep ──────────────────────────────────────────────────────────────

function MappingStep({ preview, fieldOptions, onConfirm, onBack }) {
    const { headers, auto_mapped, unmapped, preview_rows } = preview;

    // Initialise mapping state: auto-mapped pre-filled, unmapped blank
    const [mapping, setMapping] = useState(() => {
        const m = {};
        headers.forEach((h) => { m[h] = auto_mapped[h] || ""; });
        return m;
    });

    const autoCount = Object.keys(auto_mapped).length;
    const total = headers.length;

    return (
        <div className="space-y-4 py-1">
            {/* Summary */}
            <div className="p-3 rounded-md bg-accent/30 border border-border/60 text-sm text-muted-foreground">
                We recognised <span className="font-semibold text-foreground">{autoCount} of {total}</span> columns automatically.
                {unmapped.length > 0 && (
                    <> Map the remaining <span className="font-semibold text-foreground">{unmapped.length}</span> column{unmapped.length !== 1 ? "s" : ""} below, or skip them.</>
                )}
            </div>

            {/* Mapping table */}
            <div className="border border-border/60 rounded-md overflow-hidden" data-testid="mapping-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-border/60 bg-accent/20">
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider w-2/5">Your column</th>
                            <th className="px-2 py-2 text-center text-xs text-muted-foreground w-8"></th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Maps to</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                        {headers.map((h) => {
                            const isAuto = !!auto_mapped[h];
                            const firstVal = preview_rows[0]?.[h] || "";
                            return (
                                <tr key={h} className={isAuto ? "bg-green-50/40 dark:bg-green-950/20" : ""}>
                                    <td className="px-3 py-2.5">
                                        <div className="font-medium text-foreground truncate" title={h}>{h}</div>
                                        {firstVal && <div className="text-xs text-muted-foreground truncate mt-0.5">{firstVal}</div>}
                                    </td>
                                    <td className="px-1 text-center text-muted-foreground">
                                        <ArrowRight className="w-3.5 h-3.5 mx-auto" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <Select
                                                value={mapping[h] || "__skip__"}
                                                onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v === "__skip__" ? "" : v }))}
                                            >
                                                <SelectTrigger
                                                    className="h-8 text-xs"
                                                    data-testid={`map-col-${h.replace(/\s+/g, "-").toLowerCase()}`}
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__skip__" className="text-muted-foreground italic">— Skip this column —</SelectItem>
                                                    {fieldOptions.map((f) => (
                                                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {isAuto && (
                                                <span className="shrink-0 flex items-center gap-1 text-xs text-green-700 dark:text-green-400 font-medium" data-testid={`auto-mapped-badge-${h}`}>
                                                    <Check className="w-3 h-3" /> Auto
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex gap-2 pt-1">
                <Button size="sm" variant="outline" onClick={onBack}>← Back</Button>
                <Button
                    size="sm"
                    className="bg-foreground text-background hover:bg-neutral-800 ml-auto"
                    onClick={() => onConfirm(mapping)}
                    data-testid="mapping-continue-btn"
                >
                    Continue to import
                </Button>
            </div>
        </div>
    );
}

// ─── ImportTab ────────────────────────────────────────────────────────────────

function ImportTab({ type, previewEndpoint, importEndpoint, templateFilename, templateContent, rowLimit, fieldOptions }) {
    // step: 'upload' | 'analysing' | 'mapping' | 'importing' | 'result'
    const [step, setStep] = useState("upload");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reset = () => { setStep("upload"); setFile(null); setPreview(null); setResult(null); setError(null); };

    const handleFileSelected = async (f) => {
        setFile(f);
        setError(null);
        setStep("analysing");
        try {
            const fd = new FormData();
            fd.append("file", f);
            const res = await api.post(previewEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            const data = res.data;
            setPreview(data);
            if (data.unmapped.length === 0) {
                // All headers recognised — skip mapping step, import directly
                await runImport(f, null, data);
            } else {
                setStep("mapping");
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to analyse file.");
            setStep("upload");
        }
    };

    const runImport = async (fileToImport, userMapping, previewData) => {
        setStep("importing");
        try {
            const fd = new FormData();
            fd.append("file", fileToImport || file);
            if (userMapping) {
                // Only send non-empty mappings
                const cleaned = Object.fromEntries(
                    Object.entries(userMapping).filter(([, v]) => v && v !== "__skip__")
                );
                fd.append("column_mapping", JSON.stringify(cleaned));
            }
            const res = await api.post(importEndpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(res.data);
            if (res.data.imported > 0) toast.success(res.data.message);
            else toast.warning(res.data.message);
            setStep("result");
        } catch (err) {
            setError(err.response?.data?.detail || "Import failed. Please check your file.");
            toast.error(err.response?.data?.detail || "Import failed.");
            setStep("upload");
        }
    };

    return (
        <div className="space-y-4 py-2">
            {/* Template download — always visible */}
            <div className="p-3.5 border border-border/60 rounded-md bg-accent/20 space-y-2">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Download template</p>
                <p className="text-sm text-muted-foreground">
                    Use our template or upload your own file — we'll help you map the columns.
                </p>
                <Button size="sm" variant="outline"
                    onClick={() => downloadTemplate(templateFilename, templateContent)}
                    data-testid={`download-${type}-template-btn`}>
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download CSV template
                </Button>
            </div>

            {/* Step indicator */}
            {step !== "upload" && step !== "result" && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    {["Upload", "Map columns", "Import"].map((s, i) => {
                        const active = (i === 0 && (step === "analysing")) ||
                            (i === 1 && step === "mapping") ||
                            (i === 2 && step === "importing");
                        const done = (i === 0 && ["mapping", "importing", "result"].includes(step)) ||
                            (i === 1 && step === "importing");
                        return (
                            <span key={s} className="flex items-center gap-1">
                                {i > 0 && <span className="text-border">›</span>}
                                <span className={`${active ? "font-semibold text-foreground" : done ? "line-through opacity-50" : ""}`}>{s}</span>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Step content */}
            {(step === "upload" || step === "analysing") && (
                <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Maximum {rowLimit} rows per import.</p>
                    <DropZone
                        onFile={handleFileSelected}
                        selectedFile={file}
                        onClear={reset}
                        disabled={step === "analysing"}
                    />
                    {step === "analysing" && <Spinner label="Analysing your file..." />}
                    {error && <p className="text-sm text-destructive" data-testid="import-error-msg">{error}</p>}
                </div>
            )}

            {step === "mapping" && preview && (
                <div className="max-h-[380px] overflow-y-auto">
                    <MappingStep
                        preview={preview}
                        fieldOptions={fieldOptions}
                        onConfirm={(mapping) => runImport(file, mapping, preview)}
                        onBack={reset}
                    />
                </div>
            )}

            {step === "importing" && <Spinner label="Importing..." />}

            {step === "result" && result && (
                <ImportResult result={result} onReset={reset} />
            )}
        </div>
    );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function ImportModal({ open, onOpenChange, defaultTab = "contacts" }) {
    const [activeTab, setActiveTab] = useState(defaultTab);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl" data-testid="import-modal">
                <DialogHeader>
                    <DialogTitle className="font-display">Import data</DialogTitle>
                </DialogHeader>

                {/* Tabs */}
                <div className="flex border-b border-border">
                    {[{ id: "contacts", label: "Contacts" }, { id: "deals", label: "Deals" }].map((t) => (
                        <button key={t.id} onClick={() => setActiveTab(t.id)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                activeTab === t.id
                                    ? "border-foreground text-foreground"
                                    : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`import-tab-${t.id}`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === "contacts" && (
                    <ImportTab
                        type="contacts"
                        previewEndpoint="/contacts/import/preview"
                        importEndpoint="/contacts/import/csv"
                        templateFilename="closvio_contacts_template.csv"
                        templateContent={CONTACT_TEMPLATE}
                        rowLimit={500}
                        fieldOptions={CONTACT_FIELDS}
                    />
                )}
                {activeTab === "deals" && (
                    <ImportTab
                        type="deals"
                        previewEndpoint="/deals/import/preview"
                        importEndpoint="/deals/import/csv"
                        templateFilename="closvio_deals_template.csv"
                        templateContent={DEAL_TEMPLATE}
                        rowLimit={200}
                        fieldOptions={DEAL_FIELDS}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
